// SaveIndicator.jsx

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

// Context
import { useTheme } from 'context/ThemeContext';

// Services
import { uploadService } from 'services/nodes/NodeUploadService';

// CSS
import './SaveIndicator.css';

// Icons
import Icon from 'svg/Icon';

const getUploadStatusLabel = (upload) => {
  if (upload?.status === 'completed') return 'Upload complete';
  if (upload?.status === 'error') return 'Upload failed';
  return 'Upload cancelled';
};

const getUploadErrorMessage = (upload) => {
  const errorMessage = String(upload?.error?.message || '').trim();
  if (!errorMessage) return 'The upload could not be completed.';
  return errorMessage;
};

/**
 * SaveIndicator
 * 
 * Displays active and recent upload/save operations.
 * 
 * Shows progress bars for active uploads/saves and completion status for recent ones.
 * 
 * @returns {ReactNode} SaveIndicator component.
 */
const SaveIndicator = () => {

  // ----- STATE ----- //

  const [uploadStatus, setUploadStatus] = useState(uploadService.getStatus());
  const [hoveredCancelId, setHoveredCancelId] = useState('');

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- EFFECTS ----- //

  useEffect(() => {
    const unsubscribeUploads = uploadService.subscribe(setUploadStatus);
    return () => {
      unsubscribeUploads();
    };
  }, []);

  const { activeUploads, completedUploads } = uploadStatus;

  /**
   * Filters to recent completed uploads within the last 10 seconds.
   */
  const recentCutoff = Date.now() - (10 * 1000); // 10 seconds ago
  const recentFailedUploads = completedUploads
    .filter((upload) => upload.completedAt && upload.completedAt > recentCutoff && upload.status === 'error')
    .slice(-3); // Show last 3 recent failed
  const recentCompletedUploads = completedUploads
    .filter((upload) => upload.completedAt && upload.completedAt > recentCutoff && upload.status !== 'error')
    .slice(-3); // Show last 3 recent completed

  const hasVisibleActivity =
    activeUploads.length > 0 ||
    recentFailedUploads.length > 0 ||
    recentCompletedUploads.length > 0;

  if (!hasVisibleActivity) return null;

  // ----- RENDER ----- //

  // Portal to body so modal overlay backdrop-filter cannot cover this toast.
  const portalRoot = document.body;

  return ReactDOM.createPortal(
    <div
      className="save-indicator-container"
      style={{
        '--save-indicator-completed-border': theme.success,
        '--save-indicator-completed-surface': theme.success,
        '--save-indicator-error-border': theme.warning,
        '--save-indicator-error-surface': theme.warning,
      }}
    >

      {activeUploads.map(upload => (

        <div className="save-indicator-item active" key={upload.id}>

          <Icon
            iconKey="upload"
            width={16}
            height={16}
            color={theme.foreground}
          />

          {/* TEXT */}
          <span className="save-indicator-text">
            Uploading {Math.min((upload.completedFiles || 0) + 1, upload.totalFiles || 0)}/{upload.totalFiles || 0} items
          </span>

          {/* PROGRESS */}
          <div className="save-indicator-progress">

            {/* PROGRESS BAR */}
            <div
              className="save-indicator-progress-bar"
              style={{
                width: `${upload.progress || 0}%`,
              }}
            />

          </div>

          {/* CANCEL BUTTON */}
          <button
            className="save-indicator-cancel"
            style={{
              backgroundColor: hoveredCancelId === upload.id ? theme.backgroundHover : theme.backgroundMuted,
              transform: hoveredCancelId === upload.id ? 'scale(1.1)' : 'scale(1)',
            }}
            title="Cancel upload"
            onClick={() => uploadService.cancelUpload(upload.id)}
            onMouseEnter={() => setHoveredCancelId(upload.id)}
            onMouseLeave={() => setHoveredCancelId('')}
          >
            <Icon
              iconKey="close"
              width={16}
              height={16}
            />
          </button>

        </div>
      ))}

      {recentFailedUploads.map(upload => (

        <div className="save-indicator-item failed" key={upload.id}>

          <Icon
            iconKey="close"
            width={16}
            height={16}
            color={theme.foreground}
          />

          {/* MESSAGE */}
          <div className="save-indicator-message">

            {/* TEXT */}
            <span className="save-indicator-text">
              {getUploadStatusLabel(upload)}
            </span>

            {/* SUBTEXT */}
            <span className="save-indicator-subtext">
              {getUploadErrorMessage(upload)}
            </span>

          </div>

        </div>
      ))}

      {recentCompletedUploads.map(upload => (

        <div className="save-indicator-item completed" key={upload.id}>

          <Icon
            iconKey="check"
            width={16}
            height={16}
            color={theme.highlightForeground}
          />

          {/* TEXT */}
          <span className="save-indicator-text">
            {getUploadStatusLabel(upload)}
          </span>

        </div>
      ))}

    </div>,
    portalRoot,
  );
};

// Dev Tools
SaveIndicator.displayName = 'SaveIndicator';

export default SaveIndicator;
